import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, useTextoDeError } from '../componentes/ui/estados';
import {
  CajaDelEfectivo,
  cajaDelEfectivo,
  useCajasAbiertas,
  type CajaFija,
} from '../componentes/CajaDelEfectivo';

interface Pendiente {
  readonly movimientoId: string;
  readonly concepto: string | null;
  readonly vencimiento: string | null;
  readonly importe: string;
  readonly pendiente: string;
}

interface MedioElegido {
  readonly clave: string;
  readonly medioDePago: string;
  readonly importe: string;
  /** Sólo cuando el medio es cheque. Los tres datos son obligatorios y el servidor los exige. */
  readonly numero?: string;
  readonly banco?: string;
  readonly fechaDeCobro?: string;
}

const MEDIOS = ['efectivo', 'transferencia', 'debito', 'credito', 'cheque', 'otro'] as const;

/** Cuatro decimales son precisión interna; acá se mira con dos. */
function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/** Suma de textos con dos decimales, sin pasar por coma flotante. */
function sumarEnCentavos(importes: readonly string[]): number {
  return importes.reduce((suma, valor) => {
    const [entera = '0', fraccion = ''] = valor.replace(',', '.').split('.');
    const centavos = Number(entera) * 100 + Number(fraccion.padEnd(2, '0').slice(0, 2));
    return suma + (Number.isNaN(centavos) ? 0 : centavos);
  }, 0);
}

function deCentavos(centavos: number): string {
  const signo = centavos < 0 ? '-' : '';
  const absoluto = Math.abs(centavos);
  return `${signo}${String(Math.floor(absoluto / 100))}.${String(absoluto % 100).padStart(2, '0')}`;
}

/**
 * Cobrar: con qué paga y a qué se aplica.
 *
 * La propuesta la arma el servidor con el mismo criterio que aplica si nadie dice nada —lo más
 * viejo primero—, y acá se puede cambiar. Lo que sobra se muestra como "a cuenta" **antes** de
 * confirmar: enterarse después de que el cobro no canceló lo que se creía es cómo se pierde la
 * confianza en la cuenta corriente.
 *
 * Los importes se suman en centavos enteros y no con coma flotante: es plata, y la regla 2 del
 * proyecto vale también del lado de la pantalla.
 */
export function DialogoDeCobranza({
  terceroId,
  nombre,
  abierto,
  onCerrar,
  cajaFija,
}: {
  readonly terceroId: string | null;
  readonly nombre: string;
  readonly abierto: boolean;
  readonly onCerrar: () => void;
  /** La caja ya puesta, cuando se cobra desde la pantalla de caja (E37). */
  readonly cajaFija?: CajaFija | undefined;
}): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const [medios, setMedios] = useState<MedioElegido[]>([
    { clave: 'primero', medioDePago: 'efectivo', importe: '' },
  ]);
  /**
   * Lo que se aplica a cada comprobante. `null` significa "todavía nadie lo tocó": ahí manda la
   * propuesta, que se recalcula sola mientras se escribe el importe. En cuanto alguien edita una
   * línea deja de moverse: nada más molesto que un número que se corrige solo mientras se lo
   * escribe.
   */
  const [aplicado, setAplicado] = useState<Record<string, string> | null>(null);
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  const [cajaElegida, setCajaElegida] = useState('');

  const pendientes = useQuery({
    queryKey: ['pendientes', terceroId],
    enabled: abierto && terceroId !== null,
    queryFn: () => pedir<Pendiente[]>(`/cuentas-corrientes/${terceroId ?? ''}/pendientes`),
  });

  const totalCobrado = sumarEnCentavos(medios.map((medio) => medio.importe));

  // El efectivo entra a una caja (E37): sin ella, el arqueo lo encontraba como sobrante.
  const conEfectivo = medios.some(
    (medio) => medio.medioDePago === 'efectivo' && sumarEnCentavos([medio.importe]) > 0,
  );
  const abiertas = useCajasAbiertas(abierto && conEfectivo);
  const puestoDeCajaId = conEfectivo ? cajaDelEfectivo(cajaFija, cajaElegida, abiertas) : '';
  const faltaLaCaja = conEfectivo && abiertas.length > 0 && puestoDeCajaId === '';

  // La propuesta es un valor derivado y no estado: calcularla en un efecto y guardarla obligaría
  // a un renderizado de más por cada tecla, y a mantener sincronizadas dos copias del mismo dato.
  const propuesta = useMemo(() => {
    let restante = totalCobrado;
    const partes: Record<string, string> = {};
    for (const deuda of pendientes.data ?? []) {
      if (restante <= 0) break;
      const aplicar = Math.min(restante, sumarEnCentavos([deuda.pendiente]));
      partes[deuda.movimientoId] = deCentavos(aplicar);
      restante -= aplicar;
    }
    return partes;
  }, [totalCobrado, pendientes.data]);

  const asignado = aplicado ?? propuesta;
  const totalImputado = sumarEnCentavos(Object.values(asignado));
  const aCuenta = totalCobrado - totalImputado;

  const cobrar = useMutation({
    mutationFn: () =>
      pedir<{ numero: string; total: string; saldo: string }>('/cuentas-corrientes/cobranzas', {
        method: 'POST',
        headers: { 'Idempotency-Key': globalThis.crypto.randomUUID() },
        cuerpo: {
          terceroId,
          cobros: medios
            .filter((medio) => sumarEnCentavos([medio.importe]) > 0)
            .map((medio) => ({
              medioDePago: medio.medioDePago,
              importe: deCentavos(sumarEnCentavos([medio.importe])),
              // El cheque viaja con su papel. Sin número, banco y fecha de cobro no se puede
              // depositar ni endosar después, así que el servidor lo rechaza y hace bien.
              ...(medio.medioDePago === 'cheque'
                ? {
                    cheque: {
                      numero: (medio.numero ?? '').trim(),
                      banco: (medio.banco ?? '').trim(),
                      fechaDeCobro: medio.fechaDeCobro ?? '',
                    },
                  }
                : {}),
            })),
          imputaciones: Object.entries(asignado)
            .filter(([, importe]) => sumarEnCentavos([importe]) > 0)
            .map(([movimientoId, importe]) => ({ movimientoId, importe })),
          ...(observaciones.trim() === '' ? {} : { observaciones: observaciones.trim() }),
          ...(puestoDeCajaId === '' ? {} : { puestoDeCajaId }),
        },
      }),
    onSuccess: async (respuesta) => {
      setHecho(
        t('cuentas.cobrado', {
          numero: respuesta.numero,
          total: respuesta.total,
          saldo: respuesta.saldo,
        }),
      );
      setMedios([{ clave: 'primero', medioDePago: 'efectivo', importe: '' }]);
      setAplicado(null);
      setObservaciones('');
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-corrientes'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['pendientes'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cuenta-corriente'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['movimientos-de-caja'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['arqueo'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <Dialogo
      abierto={abierto}
      titulo={t('cuentas.cobranza', { cliente: nombre })}
      onCerrar={() => {
        setHecho(null);
        setError(null);
        onCerrar();
      }}
      acciones={
        <Boton
          tono="principal"
          cargando={cobrar.isPending}
          disabled={totalCobrado <= 0 || faltaLaCaja}
          onClick={() => {
            cobrar.mutate();
          }}
        >
          {t('cuentas.registrar')}
        </Boton>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {medios.map((medio, indice) => (
            <div key={medio.clave} className="flex items-end gap-2">
              <Selector
                etiqueta={t('cuentas.medioDePago')}
                value={medio.medioDePago}
                onChange={(evento) => {
                  const elegido = evento.target.value;
                  setMedios((previos) =>
                    previos.map((fila, i) =>
                      i === indice ? { ...fila, medioDePago: elegido } : fila,
                    ),
                  );
                }}
              >
                {MEDIOS.map((valor) => (
                  <option key={valor} value={valor}>
                    {t(`venta.${valor}`, valor)}
                  </option>
                ))}
              </Selector>
              <Campo
                etiqueta={t('cuentas.importe')}
                inputMode="decimal"
                value={medio.importe}
                onChange={(evento) => {
                  const valor = evento.target.value;
                  setMedios((previos) =>
                    previos.map((fila, i) => (i === indice ? { ...fila, importe: valor } : fila)),
                  );
                }}
              />
              {medios.length > 1 ? (
                <Boton
                  tono="secundario"
                  onClick={() => {
                    setMedios((previos) => previos.filter((_, i) => i !== indice));
                  }}
                >
                  {t('cuentas.quitarMedio')}
                </Boton>
              ) : null}
            </div>
          ))}
          {/*
            El papel del cheque. Aparece pegado al medio que lo pide y no en una sección aparte:
            quien cobra tiene el cheque en la mano en ese momento, y pedírselo tres campos más
            abajo es cómo se termina cargando "0000" para salir del paso.
          */}
          {medios.map((medio, indice) =>
            medio.medioDePago !== 'cheque' ? null : (
              <div key={`cheque-${medio.clave}`} className="flex flex-wrap items-end gap-2 pl-2">
                <Campo
                  etiqueta={t('cuentas.numeroDeCheque')}
                  value={medio.numero ?? ''}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setMedios((previos) =>
                      previos.map((fila, i) => (i === indice ? { ...fila, numero: valor } : fila)),
                    );
                  }}
                />
                <Campo
                  etiqueta={t('cuentas.bancoDelCheque')}
                  value={medio.banco ?? ''}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setMedios((previos) =>
                      previos.map((fila, i) => (i === indice ? { ...fila, banco: valor } : fila)),
                    );
                  }}
                />
                <Campo
                  etiqueta={t('cuentas.fechaDeCobro')}
                  type="date"
                  value={medio.fechaDeCobro ?? ''}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setMedios((previos) =>
                      previos.map((fila, i) =>
                        i === indice ? { ...fila, fechaDeCobro: valor } : fila,
                      ),
                    );
                  }}
                />
              </div>
            ),
          )}
          <div>
            <Boton
              tono="secundario"
              onClick={() => {
                setMedios((previos) => [
                  ...previos,
                  { clave: String(previos.length + 1), medioDePago: 'transferencia', importe: '' },
                ]);
              }}
            >
              {t('cuentas.agregarMedio')}
            </Boton>
          </div>
        </div>

        {conEfectivo ? (
          <CajaDelEfectivo
            fija={cajaFija}
            abiertas={abiertas}
            valor={cajaElegida}
            onCambiar={setCajaElegida}
          />
        ) : null}

        <div>
          <p className="text-xs font-medium text-texto-tenue">{t('cuentas.pendientes')}</p>
          {pendientes.isPending ? <Cargando /> : null}
          {pendientes.isSuccess && pendientes.data.length === 0 ? (
            <p className="mt-1 text-xs text-texto-tenue">{t('cuentas.sinPendientes')}</p>
          ) : null}
          {pendientes.isSuccess && pendientes.data.length > 0 ? (
            <>
              <p className="mb-1 text-xs text-texto-tenue">{t('cuentas.propuesta')}</p>
              <ul className="flex flex-col divide-y divide-borde">
                {pendientes.data.map((deuda) => (
                  <li key={deuda.movimientoId} className="flex items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      {deuda.concepto ?? '—'}
                      <span className="block text-xs text-texto-tenue">
                        {t('cuentas.pendiente')}: {conDosDecimales(deuda.pendiente)}
                        {deuda.vencimiento === null ? '' : ` · ${deuda.vencimiento}`}
                      </span>
                    </span>
                    <input
                      aria-label={`${t('cuentas.aImputar')} ${deuda.concepto ?? ''}`}
                      inputMode="decimal"
                      className="numero w-28 rounded-marco border border-borde bg-superficie px-2 py-1 text-right text-sm"
                      value={asignado[deuda.movimientoId] ?? ''}
                      onChange={(evento) => {
                        const valor = evento.target.value;
                        setAplicado({ ...asignado, [deuda.movimientoId]: valor });
                      }}
                    />
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>

        <dl className="flex flex-col gap-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-texto-tenue">{t('cuentas.totalCobrado')}</dt>
            <dd className="numero">{deCentavos(totalCobrado)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-texto-tenue">{t('cuentas.totalImputado')}</dt>
            <dd className="numero">{deCentavos(totalImputado)}</dd>
          </div>
          <div className="flex justify-between font-semibold">
            <dt>{t('cuentas.aCuenta')}</dt>
            <dd className="numero">{deCentavos(aCuenta)}</dd>
          </div>
        </dl>

        <Campo
          etiqueta={t('cuentas.observaciones')}
          maxLength={500}
          value={observaciones}
          onChange={(evento) => {
            setObservaciones(evento.target.value);
          }}
        />

        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
        {hecho === null ? null : (
          <p role="status" className="text-xs text-bien">
            {hecho}
          </p>
        )}
      </div>
    </Dialogo>
  );
}
